/*
 * The photographs a packet is read from — the nutrition spec, Own foods. One camera, and under
 * it a tray with a place for each photograph: the front and the back to begin with, and any
 * further ones added by name. One place at a time is the one the next photograph fills.
 *
 * Kept apart from the page so what happens to the tray on each press is said in one place and
 * held by tests, rather than spread through handlers.
 */

/** What a photograph of a packet is of. The first two are the sides every tray starts with. */
export type PacketPhotoKind = 'front' | 'back' | 'ingredients' | 'barcode' | 'side' | 'other'

/** What a further photograph can be named, in the order they are offered. */
export type FurtherKind = Exclude<PacketPhotoKind, 'front' | 'back'>
export const FURTHER_KINDS: readonly FurtherKind[] = ['ingredients', 'barcode', 'side', 'other']

/** The most photographs one packet is read from, the same stop the server holds. */
export const MAX_PACKET_PHOTOS = 4

export interface PacketPlace {
  readonly id: number
  readonly kind: PacketPhotoKind
  /** The photograph, base64 JPEG, or null where the place is still empty. */
  readonly image: string | null
}

export interface PacketTray {
  readonly places: readonly PacketPlace[]
  /** The place the next photograph fills, or null where none is. */
  readonly filling: number | null
  readonly nextId: number
}

/** What the tray is sent to the server as. */
export interface PacketPhotos {
  readonly front: string | null
  readonly back: string | null
  readonly more: readonly { readonly kind: FurtherKind; readonly image: string }[]
}

/** A tray as the photographing opens: both sides waiting, the front first. */
export function emptyTray(): PacketTray {
  return {
    places: [
      { id: 0, kind: 'front', image: null },
      { id: 1, kind: 'back', image: null },
    ],
    filling: 0,
    nextId: 2,
  }
}

const isSide = (kind: PacketPhotoKind) => kind === 'front' || kind === 'back'

/** How many photographs the tray holds. */
export function photosTaken(tray: PacketTray): number {
  return tray.places.filter((place) => place.image !== null).length
}

export function fillingPlace(tray: PacketTray): PacketPlace | null {
  return tray.places.find((place) => place.id === tray.filling) ?? null
}

/** The packet can be read once there is anything to read it from. */
export function canRead(tray: PacketTray): boolean {
  return photosTaken(tray) > 0
}

/**
 * Whether a further photograph is offered: whatever else the tray holds, the sides included,
 * for as long as there is room for another photograph.
 */
export function canAddFurther(tray: PacketTray): boolean {
  return photosTaken(tray) < MAX_PACKET_PHOTOS
}

/**
 * A further place, whose image is empty, only lives while it is the one being filled: going
 * anywhere else gives up on it, so a tray never stands holding a named hole nobody asked for.
 */
function withoutEmptyFurther(places: readonly PacketPlace[], keep: number | null) {
  return places.filter((place) => isSide(place.kind) || place.image !== null || place.id === keep)
}

/**
 * The place pressed becomes the one the next photograph fills. An empty one only while there
 * is room for another photograph; a filled one is taken again, and keeps its photograph until
 * the new one lands.
 */
export function aimAt(tray: PacketTray, id: number): PacketTray {
  const place = tray.places.find((it) => it.id === id)
  if (place === undefined) {
    return tray
  }
  if (place.image === null && photosTaken(tray) >= MAX_PACKET_PHOTOS) {
    return tray
  }
  return { ...tray, places: withoutEmptyFurther(tray.places, id), filling: id }
}

/**
 * A photograph lands in the place being filled. The next empty place after it is then the one
 * being filled, and where there is none — or the tray is full — no place is.
 */
export function land(tray: PacketTray, image: string): PacketTray {
  const at = tray.places.findIndex((place) => place.id === tray.filling)
  if (at === -1) {
    return tray
  }
  const places = tray.places.map((place, index) => (index === at ? { ...place, image } : place))
  const full = places.filter((place) => place.image !== null).length >= MAX_PACKET_PHOTOS
  const next = full ? undefined : places.slice(at + 1).find((place) => place.image === null)
  return { ...tray, places, filling: next?.id ?? null }
}

/**
 * A further photograph named: its place joins the tray and is the one the next photograph
 * fills. A further place standing empty is given up for it, as it is for any other press.
 */
export function addFurther(tray: PacketTray, kind: FurtherKind): PacketTray {
  if (!canAddFurther(tray)) {
    return tray
  }
  const id = tray.nextId
  return {
    places: [...withoutEmptyFurther(tray.places, null), { id, kind, image: null }],
    filling: id,
    nextId: id + 1,
  }
}

/**
 * A photograph removed. A side stays in the tray, empty, and where nothing is being filled it
 * is the place filled next; a further photograph goes with its place.
 */
export function removePhoto(tray: PacketTray, id: number): PacketTray {
  const place = tray.places.find((it) => it.id === id)
  if (place === undefined) {
    return tray
  }
  if (isSide(place.kind)) {
    return {
      ...tray,
      places: tray.places.map((it) => (it.id === id ? { ...it, image: null } : it)),
      filling: tray.filling ?? id,
    }
  }
  return {
    ...tray,
    places: tray.places.filter((it) => it.id !== id),
    filling: tray.filling === id ? null : tray.filling,
  }
}

export function photosOf(tray: PacketTray): PacketPhotos {
  const image = (kind: PacketPhotoKind) =>
    tray.places.find((place) => place.kind === kind)?.image ?? null
  return {
    front: image('front'),
    back: image('back'),
    more: tray.places.flatMap((place) =>
      !isSide(place.kind) && place.image !== null ? [{ kind: place.kind, image: place.image }] : [],
    ),
  }
}

/** What a place is called. */
export function placeName(kind: PacketPhotoKind): string {
  switch (kind) {
    case 'front':
      return 'Front'
    case 'back':
      return 'Back'
    case 'ingredients':
      return 'Ingredients'
    case 'barcode':
      return 'Barcode'
    case 'side':
      return 'Side panel'
    case 'other':
      return 'Other'
  }
}

/** What the camera asks for while a place is being filled, or once none is. */
export function cameraAsks(tray: PacketTray): string {
  const filling = fillingPlace(tray)
  switch (filling?.kind) {
    case 'front':
      return 'The front — its name and its brand'
    case 'back':
      return 'The back — the nutrition table'
    case 'ingredients':
      return 'The ingredients list'
    case 'barcode':
      return 'The barcode, close up'
    case 'side':
      return 'Another side of the packet'
    case 'other':
      return 'Anything else on the packet'
    case undefined:
      return photosTaken(tray) < MAX_PACKET_PHOTOS
        ? 'Read the packet, or add another photo'
        : 'Read the packet'
  }
}
